import React from 'react';
import TooltipIcon from '~/components/TooltipIcon/TooltipIcon';
import { FlowChartIcon, XCircleIcon } from '@sumologic/yama-ds-icons';

const iconsInRowMap = new Map([
    ['Delete', { icon: 'trash2' }],
    ['Remove', { icon: 'trash2' }],
    ['Discard', { icon: 'trash2' }],
    ['Dismiss', { icon: 'trash2' }],

    ['Reassign', { icon: 'sync' }],
    ['Reschedule', { icon: 'sync' }],
    ['Use in correlation', { icon: 'sync' }],

    ['Clone incident', { icon: 'files' }],
    ['Grab incident', { icon: 'files' }],
    ['Clone', { icon: 'files' }],

    ['Add investigator', { icon: 'user-plus' }],
    ['Grab', { icon: 'user-plus' }],

    ['Playbook result', { icon: 'list' }],
    ['Results', { icon: 'list' }],

    ['Kill', { icon: 'cross-circle' }],
    ['Kill playbook', { icon: 'cross-circle' }],

    ['Run', { icon: 'play-circle' }],
    ['Run playbook', { icon: 'play-circle' }],

    ['Approve', { icon: 'thumbs-up2' }],
    ['Authorize', { icon: 'thumbs-up2' }],

    ['Decline', { icon: 'thumbs-down2' }],
    ['Not authorize', { icon: 'thumbs-down2' }],

    ['Restore', { icon: 'redo2' }],
    ['Reset', { icon: 'sync' }],

    ['Lock', { icon: 'lock' }],
    ['Unlock', { icon: 'unlock' }],

    ['Favorite', { icon: 'star' }],
    ['Unfavorite', { icon: 'star-fill' }],

    ['Close', { icon: 'check' }],
    ['Reopen', { icon: 'undo2' }],

    ['Convert to incident', { icon: 'shield-alert' }],
    ['Go to incident', { icon: 'shield-check2' }],

    ['Export', { icon: 'exit-down' }],
    ['Export in customization', { icon: 'floppy-disk' }],

    ['Alert', { icon: 'alarm' }],
    ['Approve & close', { icon: 'check' }],
    ['Change owner', { icon: 'group-work' }],
    ['Details', { icon: 'magnifier' }],
    ['Duplicate', { icon: 'duplicate' }],
    ['Edit', { icon: 'pencil' }],
    ['Email', { icon: 'envelope' }],
    ['Force user logout', { icon: 'exit' }],
    ['Go to element', { icon: 'arrow-right-circle' }],
    ['Grab and open', { icon: 'user-arrow' }],
    ['Move into group', { icon: 'users' }],
    ['Notification', { icon: 'bubble-dots' }],
    ['Push field', { icon: 'paper-plane' }],
    ['Publish', { icon: 'clipboard-check' }],
    ['Required', { icon: 'warning' }],
    ['Synchronization status', { icon: 'circle2' }],
    ['Use as filter', { icon: 'funnel' }],
    ['Upload', { icon: 'upload2' }],
    ['Usage information', { icon: 'chart-growth' }],
    ['Visibility', { icon: 'eye' }],
    [
        'Playbook',
        {
            IconComponent: FlowChartIcon,
            tooltip: 'Go to playbook'
        }
    ],
    [
        'Kill',
        {
            IconComponent: XCircleIcon,
            tooltip: 'Kill Execution'
        }
    ]

    //, ['', { icon: '' }]
    //, ['', { icon: '', tooltip:'' }]
]);

/**
 * Evaluates tooltip value based on type (function or static value)
 * @param {string|Function} tooltip - Tooltip value or function
 * @param {Object} rowNode - AG Grid row node
 * @param {Object} recognized - Recognized icon configuration
 * @param {string} name - Icon name
 * @returns {string} Evaluated tooltip value
 */
const evaluateTooltip = (tooltip, rowNode, recognized, name) => {
    if (typeof tooltip === 'function') {
        return tooltip(rowNode);
    }
    return tooltip || recognized.tooltip || name;
};

/*
    iconInRow descriptor:
    {
        showCondition: ({data}) => data.something,
        isVisible = true,
        disabled = false,
        disableCondition: ({data}) => data.something,
        tooltip: ''
        tooltipValueFormatter: (node,tooltipValue) => string,
        className: '',
        classNameValueFormatter: (node,className) => string,
        fn: row => {},
        fixed: true,
        icon: '',
        name: '',
    },
*/

// 🔒
// prettier-ignore
export const createIconsInRowGroup = (iconsInRowDescriptions, groupName, params, forceTooltip) => {
    let iconsInRowGroup = null;
    if (iconsInRowDescriptions.length) {
        const rowNode = params.node;
        const iconsInRow = [];
        let key = 0;
        for (const iconInRowDescriptor of iconsInRowDescriptions) {
            if (iconInRowDescriptor) {
                const {
                    isVisible = true,
                    showCondition,
                    disabled = false,
                    disableCondition,
                    tooltip,
                    tooltipValueFormatter,
                    className = null,
                    classNameValueFormatter,
                    name,
                    icon,
                    fn,
                    testId,
                } = iconInRowDescriptor;
                if (
                    isVisible &&
                    iconsInRowMap.has(name)
                ) {
                    if (showCondition?.(rowNode) ?? true) {
                        const recognized = iconsInRowMap.get(name);
                        const isDisabled = disabled || (disableCondition?.(rowNode) ?? false);
                        const tooltipValue = evaluateTooltip(tooltip, rowNode, recognized, name);
                        key = iconsInRow.push(
                            <TooltipIcon
                                key={key}
                                className="ag-icon-in-row"
                                type={
                                    icon?.length
                                        ? icon
                                        : recognized.icon
                                }
                                iconComponent={recognized.IconComponent}
                                message={
                                    !forceTooltip && isDisabled
                                        ? null
                                        : tooltipValueFormatter
                                            ? tooltipValueFormatter(rowNode, tooltipValue)
                                            : tooltipValue
                                }
                                buttonAttrs={{
                                    className:
                                        classNameValueFormatter
                                            ? classNameValueFormatter(rowNode, className)
                                            : className,
                                    disabled:
                                        isDisabled,
                                    ...(testId ? {'data-sumo-test': testId} : {}),
                                    onClick:
                                        isDisabled
                                            ? null
                                            : fn
                                                ? syntheticEvent => {
                                                      fn(
                                                          rowNode,
                                                          params,
                                                          syntheticEvent
                                                      );
                                                      return false;
                                                  }
                                                : null
                                }}
                            />
                        );
                    }
                }
            }
        }
        if (iconsInRow.length) {
            const cssClasses = ['ag-icon-group','icon-group'];
            if (groupName) {
                cssClasses.push(groupName);
            }
            iconsInRowGroup = (
                <span className={cssClasses.join('\u0020')}>
                    {iconsInRow}
                </span>
            );
        }
    }
    return iconsInRowGroup;
};
